import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import ConsentCheckbox from "@/components/site/ConsentCheckbox";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema, freeProgramOfferSchema } from "@/components/seo/schema";
import { FREE_PROGRAM } from "@/lib/freeProgram";

const BUSINESS_TYPES = [
  "Blue Collar Business",
  "Local Service Company",
  "Small Business",
  "Startup",
  "Corporate Business",
  "Other",
];

const GOALS = [
  "Generate more leads",
  "Build credibility & trust",
  "Improve online visibility / SEO",
  "Replace an outdated website",
  "Launch a brand new business",
  "Other",
];

const EMPTY = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  business_type: "",
  business_state: "",
  current_website: "",
  project_goal: "",
  message: "",
};

export default function Apply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(EMPTY)) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete every field. Use N/A or Unknown where it does not apply.");
        return;
      }
    }
    if (!consent) {
      toast.error("Please review and agree to the Terms & Conditions to continue.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/apply", { ...form, consent_agreement: true });
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-apply">
      <Seo
        title="Apply For A Free Website | Equinox Systems"
        description="Apply for the Free Custom Website Program. $0 upfront build cost, hosting & maintenance at $200/month, 6-month minimum, ownership transfer included."
        path="/apply"
        schemas={[freeProgramOfferSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Apply", path: "/apply" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-10">
          {/* Program summary */}
          <aside className="lg:col-span-2">
            <div className="eq-chip"><Sparkles className="w-3 h-3" /> Free Custom Website Program</div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.06]">
              Apply For A <span className="eq-gradient-text text-glow">Free Website</span>
            </h1>
            <p className="mt-5 text-gray-400 leading-relaxed">
              No upfront website development cost. You only pay for hosting, maintenance, and support.
            </p>
            <div className="mt-8 glass rounded-2xl p-6 space-y-3 text-sm">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-gray-400">Website Build</span>
                <span className="text-white font-semibold">{FREE_PROGRAM.build}</span>
              </div>
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-gray-400">Hosting &amp; Maintenance</span>
                <span className="text-white font-semibold">{FREE_PROGRAM.monthly}{FREE_PROGRAM.monthlySuffix}</span>
              </div>
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-gray-400">Minimum Commitment</span>
                <span className="text-white font-semibold">{FREE_PROGRAM.commitment}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Ownership Transfer</span>
                <span className="text-white font-semibold">{FREE_PROGRAM.ownership}</span>
              </div>
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-2.5">
              {FREE_PROGRAM.features.slice(0, 6).map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-6" data-testid="apply-form">
              <SectionHeader eyebrow="Application" title="Tell us about your business" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *"><Input data-testid="apply-name" value={form.full_name} onChange={setField("full_name")} className="bg-white/5 border-white/10 text-white" placeholder="Your full name" /></Field>
                <Field label="Business Name *"><Input data-testid="apply-business" value={form.business_name} onChange={setField("business_name")} className="bg-white/5 border-white/10 text-white" placeholder="Company name" /></Field>
                <Field label="Email *"><Input data-testid="apply-email" type="email" value={form.email} onChange={setField("email")} className="bg-white/5 border-white/10 text-white" placeholder="you@business.com" /></Field>
                <Field label="Phone *"><Input data-testid="apply-phone" value={form.phone} onChange={setField("phone")} className="bg-white/5 border-white/10 text-white" placeholder="(555) 000-1234" /></Field>
                <Field label="Business Type *">
                  <Select value={form.business_type} onValueChange={setField("business_type")}>
                    <SelectTrigger data-testid="apply-business-type" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Business State *"><Input data-testid="apply-state" value={form.business_state} onChange={setField("business_state")} className="bg-white/5 border-white/10 text-white" placeholder="e.g. Texas" /></Field>
                <Field label="Current Website URL *"><Input data-testid="apply-website" value={form.current_website} onChange={setField("current_website")} className="bg-white/5 border-white/10 text-white" placeholder="https:// or N/A" /></Field>
                <Field label="Primary Goal *">
                  <Select value={form.project_goal} onValueChange={setField("project_goal")}>
                    <SelectTrigger data-testid="apply-goal" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select goal" /></SelectTrigger>
                    <SelectContent>{GOALS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Tell us about your project *">
                <Textarea data-testid="apply-message" rows={5} value={form.message} onChange={setField("message")} className="bg-white/5 border-white/10 text-white" placeholder="What does your business do, who are your customers, and what do you want your website to achieve?" />
              </Field>

              <ConsentCheckbox checked={consent} onChange={setConsent} testid="apply-consent" />

              <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="apply-submit-btn">
                {submitting ? "Submitting..." : "Apply For A Free Website"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-sm">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
